import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded label-small ring-offset-background transition-colors duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none dark:disabled:bg-gray-dark-900 dark:disabled:border-gray-dark-200 disabled:border disabled:text-separator dark:disabled:text-gray-dark-1200 tracking-[-.0.6%] [&_svg]:transition-colors [&_svg]:duration-300",
  {
    variants: {
      variant: {
        primary:
          "bg-button-primary text-button-primary-foreground border border-button-primary-border",
        standout:
          "bg-blue-100 text-white",
        default:
          "bg-button-default text-button-default-foreground border border-button-default-border hover:button-default-accent",
        outline:
          "border border-border bg-background text-foreground rounded-[8px] shadow-[-8px_8px_8px_0px_rgba(0,0,0,0.02)] hover:bg-gray-light-500 dark:hover:bg-border",
        ghost:
          "hover:bg-button-ghost-accent hover:text-button-ghost-accent-foreground border-0 [&_svg]:hover:text-button-ghost-accent-icon",
        link: "text-primary underline-offset-4 hover:underline",
        error:
          "border border-red-light-400 dark:border-0 dark:bg-red-dark-400 hover:bg-opacity-[32%] text-red-light-800 hover:text-red-dark-300 dark:bg-opacity-25"
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-8 rounded-[8px] px-3",
        sm: "h-9 rounded-[6px] px-3 min-h-[36px]",
        icon: "h-9 w-9"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        type="button"
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
